import { DragDropContext, type DropResult } from "@hello-pangea/dnd";
import { useDataProvider, useNotify, type Identifier } from "ra-core";
import { useCallback, useRef, useState } from "react";

import type { Task, TaskStatus } from "../../types";
import type { PendingMove } from "../pendingMoves";
import { getDropPosition } from "../positions";
import { useRefreshBoard } from "../useRefreshBoard";
import { TaskColumn } from "./TaskColumn";
import { TASK_STATUSES } from "./taskStatuses";

export interface TaskBoardProps {
  /** Server filter (without the status: the columns are the statuses). */
  filter: Record<string, unknown>;
  salesNames: Map<Identifier, string>;
  onOpen: (task: Task) => void;
}

/**
 * Task board by status. Each column paginates on its own (25 at a time);
 * dragging changes the status and the order.
 */
export const TaskBoard = ({ filter, salesNames, onOpen }: TaskBoardProps) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refreshBoard = useRefreshBoard();
  const columnsRef = useRef(new Map<TaskStatus, Task[]>());
  const [pendingMoves, setPendingMoves] = useState<
    Record<string, PendingMove<Task>>
  >({});

  const handleItemsChange = useCallback((status: TaskStatus, tasks: Task[]) => {
    columnsRef.current.set(status, tasks);
  }, []);

  const handleDragEnd = async ({
    draggableId,
    source,
    destination,
  }: DropResult) => {
    if (
      !destination ||
      (destination.droppableId === source.droppableId &&
        destination.index === source.index)
    ) {
      return;
    }
    const task = columnsRef.current
      .get(source.droppableId as TaskStatus)
      ?.find((candidate) => String(candidate.id) === draggableId);
    if (!task) return;
    const status = destination.droppableId as TaskStatus;
    const position = getDropPosition(
      (columnsRef.current.get(status) ?? []) as {
        id: Identifier;
        position: number;
      }[],
      task.id,
      destination.index,
    );
    setPendingMoves((moves) => ({
      ...moves,
      [draggableId]: { item: task, columnId: status, position },
    }));
    try {
      await dataProvider.update("tasks", {
        id: task.id,
        data: { status, position },
        previousData: task,
      });
      await refreshBoard();
    } catch (error: unknown) {
      notify(
        error instanceof Error ? error.message : "ra.notification.http_error",
        { type: "error" },
      );
    } finally {
      setPendingMoves(({ [draggableId]: _done, ...moves }) => moves);
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-4">
        {TASK_STATUSES.map((status) => (
          <TaskColumn
            key={status}
            status={status}
            filter={filter}
            pendingMoves={pendingMoves}
            onItemsChange={handleItemsChange}
            salesNames={salesNames}
            onOpen={onOpen}
          />
        ))}
      </div>
    </DragDropContext>
  );
};
